import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useWeb3 } from '../context/Web3Context';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Slider } from '../components/ui/slider';
import { toast } from 'sonner';
import { 
  Lock, Unlock, Coins, TrendingUp, Clock, Calculator,
  Wallet, Shield, Zap, Award, ChevronRight, Info
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAKING_TIERS = [
  { name: 'Bronze', minStake: 1000, apy: 8, lockPeriod: 30, color: 'bronze', icon: '🥉' },
  { name: 'Silver', minStake: 10000, apy: 12, lockPeriod: 60, color: 'silver', icon: '🥈' },
  { name: 'Gold', minStake: 50000, apy: 18, lockPeriod: 90, color: 'gold', icon: '🥇' },
  { name: 'Platinum', minStake: 100000, apy: 25, lockPeriod: 180, color: 'platinum', icon: '💎' },
];

const Staking = () => {
  const { user, token, updateUser } = useAuth();
  const { isConnected, account, connectWallet } = useWeb3();
  const [selectedTier, setSelectedTier] = useState(STAKING_TIERS[0]);
  const [stakeAmount, setStakeAmount] = useState('');
  const [unstakeAmount, setUnstakeAmount] = useState('');
  const [stakeDuration, setStakeDuration] = useState(30);
  const [isStaking, setIsStaking] = useState(false);
  const [isUnstaking, setIsUnstaking] = useState(false);
  const [stakingStats, setStakingStats] = useState({
    totalStaked: 0,
    estimatedRewards: 0,
    currentTier: 'None',
    nextReward: '0',
  });

  // Calculate potential rewards
  const calculateRewards = (amount, days, apy) => {
    if (!amount || isNaN(amount)) return 0;
    const dailyRate = apy / 365 / 100;
    return parseFloat(amount) * dailyRate * days;
  };

  const potentialRewards = calculateRewards(stakeAmount, stakeDuration, selectedTier.apy);
  const totalReturn = parseFloat(stakeAmount || 0) + potentialRewards;

  // Determine recommended tier based on amount
  useEffect(() => {
    const amount = parseFloat(stakeAmount) || 0;
    const recommendedTier = [...STAKING_TIERS].reverse().find(tier => amount >= tier.minStake) || STAKING_TIERS[0];
    if (amount >= recommendedTier.minStake) {
      setSelectedTier(recommendedTier);
    }
  }, [stakeAmount]);

  const handleStake = async () => {
    const amt = parseFloat(stakeAmount);
    if (!amt || amt <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    if (amt > (user?.aih_tokens || 0)) {
      toast.error('Insufficient tokens');
      return;
    }
    if (amt < selectedTier.minStake) {
      toast.error(`Minimum stake for ${selectedTier.name} tier is ${selectedTier.minStake.toLocaleString()} AIH`);
      return;
    }

    setIsStaking(true);
    try {
      await axios.post(`${API}/wallet/stake?amount=${amt}`, {}, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      toast.success(`Successfully staked ${amt.toLocaleString()} AIH at ${selectedTier.apy}% APY!`);
      updateUser({ 
        aih_tokens: (user.aih_tokens || 0) - amt, 
        staked_tokens: (user.staked_tokens || 0) + amt 
      });
      setStakeAmount('');
    } catch (e) { 
      toast.error('Failed to stake tokens'); 
    }
    setIsStaking(false);
  };

  const handleUnstake = async () => {
    const amt = parseFloat(unstakeAmount);
    if (!amt || amt <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    if (amt > (user?.staked_tokens || 0)) {
      toast.error('Insufficient staked tokens');
      return;
    }

    setIsUnstaking(true);
    try {
      await axios.post(`${API}/wallet/unstake?amount=${amt}`, {}, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      toast.success(`Successfully unstaked ${amt.toLocaleString()} AIH!`);
      updateUser({ 
        aih_tokens: (user.aih_tokens || 0) + amt, 
        staked_tokens: (user.staked_tokens || 0) - amt 
      });
      setUnstakeAmount('');
    } catch (e) { 
      toast.error('Failed to unstake tokens'); 
    }
    setIsUnstaking(false);
  };

  return (
    <div className="min-h-screen pt-20 pb-12 px-6" data-testid="staking-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="font-unbounded text-4xl sm:text-5xl font-bold text-white mb-4">
            AIH <span className="gradient-text">Staking</span>
          </h1>
          <p className="text-lg text-white/70 max-w-2xl mx-auto">
            Stake your AIH tokens to earn passive rewards. Higher stakes unlock better APY rates.
          </p>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="glass-panel p-5" data-testid="stat-available">
            <div className="flex items-center justify-between mb-2">
              <span className="text-white/60 text-sm">Available</span>
              <Coins className="text-[#D4AF37]" size={18} />
            </div>
            <p className="font-unbounded text-2xl font-bold text-white">
              {(user?.aih_tokens || 0).toLocaleString()}
            </p>
            <p className="text-xs text-[#D4AF37]">AIH Tokens</p>
          </div>
          <div className="glass-panel p-5" data-testid="stat-staked">
            <div className="flex items-center justify-between mb-2">
              <span className="text-white/60 text-sm">Staked</span>
              <Lock className="text-purple-400" size={18} />
            </div>
            <p className="font-unbounded text-2xl font-bold text-white">
              {(user?.staked_tokens || 0).toLocaleString()}
            </p>
            <p className="text-xs text-purple-400">Locked AIH</p>
          </div>
          <div className="glass-panel p-5" data-testid="stat-rewards">
            <div className="flex items-center justify-between mb-2">
              <span className="text-white/60 text-sm">Earned Rewards</span>
              <TrendingUp className="text-green-400" size={18} />
            </div>
            <p className="font-unbounded text-2xl font-bold text-white">
              {((user?.staked_tokens || 0) * 0.0002).toFixed(2)}
            </p>
            <p className="text-xs text-green-400">AIH Rewards</p>
          </div>
          <div className="glass-panel p-5" data-testid="stat-tier">
            <div className="flex items-center justify-between mb-2">
              <span className="text-white/60 text-sm">Current Tier</span>
              <Award className="text-[#D4AF37]" size={18} />
            </div>
            <p className="font-unbounded text-2xl font-bold text-white">
              {user?.staked_tokens >= 100000 ? 'Platinum' :
               user?.staked_tokens >= 50000 ? 'Gold' :
               user?.staked_tokens >= 10000 ? 'Silver' :
               user?.staked_tokens >= 1000 ? 'Bronze' : 'None'}
            </p>
            <p className="text-xs text-[#D4AF37]">Staking Level</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Staking Tiers */}
          <div className="lg:col-span-1 space-y-6">
            <div className="glass-panel p-6" data-testid="staking-tiers">
              <h3 className="font-unbounded text-xl font-bold text-white mb-6">Staking Tiers</h3>
              <div className="space-y-3">
                {STAKING_TIERS.map((tier) => (
                  <div 
                    key={tier.name}
                    onClick={() => setSelectedTier(tier)}
                    className={`p-4 rounded-xl border cursor-pointer transition-all ${
                      selectedTier.name === tier.name 
                        ? 'border-[#D4AF37] bg-[#D4AF37]/10' 
                        : 'border-white/10 bg-[#0A1628]/60 hover:border-white/30'
                    }`}
                    data-testid={`tier-${tier.name.toLowerCase()}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className={`tier-badge ${tier.color}`}>{tier.name}</span>
                      </div>
                      <span className="font-unbounded text-xl font-bold text-[#D4AF37]">{tier.apy}%</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-white/60">Min. Stake</span>
                      <span className="text-white">{tier.minStake.toLocaleString()} AIH</span>
                    </div>
                    <div className="flex justify-between text-sm mt-1">
                      <span className="text-white/60">Lock Period</span>
                      <span className="text-white">{tier.lockPeriod} days</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Middle & Right - Staking Actions */}
          <div className="lg:col-span-2 space-y-6">
            {/* Staking Calculator */}
            <div className="glass-panel p-6" data-testid="staking-calculator">
              <h3 className="font-unbounded text-xl font-bold text-white mb-6 flex items-center gap-2">
                <Calculator className="text-[#D4AF37]" size={24} />
                Staking Calculator
              </h3>

              <Tabs defaultValue="stake">
                <TabsList className="grid w-full grid-cols-2 bg-[#0A1628] mb-6">
                  <TabsTrigger value="stake" data-testid="stake-tab">
                    <Lock className="mr-2" size={16} /> Stake
                  </TabsTrigger>
                  <TabsTrigger value="unstake" data-testid="unstake-tab">
                    <Unlock className="mr-2" size={16} /> Unstake
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="stake" className="space-y-6">
                  {/* Amount Input */}
                  <div>
                    <label className="text-white/80 text-sm mb-2 block">Amount to Stake</label>
                    <Input
                      type="number"
                      placeholder="Enter amount"
                      value={stakeAmount}
                      onChange={(e) => setStakeAmount(e.target.value)}
                      className="input-cosmic text-lg"
                      data-testid="stake-amount-input"
                    />
                    <div className="flex justify-between mt-2">
                      <span className="text-xs text-white/40">
                        Available: {(user?.aih_tokens || 0).toLocaleString()} AIH
                      </span>
                      <button 
                        onClick={() => setStakeAmount(user?.aih_tokens?.toString() || '0')}
                        className="text-xs text-[#D4AF37] hover:underline"
                      >
                        Max
                      </button>
                    </div>
                  </div>

                  {/* Duration Slider */}
                  <div>
                    <div className="flex justify-between mb-2">
                      <label className="text-white/80 text-sm">Lock Duration</label>
                      <span className="text-[#D4AF37] font-bold">{stakeDuration} days</span>
                    </div>
                    <Slider
                      value={[stakeDuration]}
                      onValueChange={([val]) => setStakeDuration(val)}
                      min={30}
                      max={365}
                      step={30}
                      className="py-4"
                    />
                    <div className="flex justify-between text-xs text-white/40">
                      <span>30 days</span>
                      <span>365 days</span>
                    </div>
                  </div>

                  {/* Rewards Preview */}
                  <div className="bg-[#0A1628]/80 rounded-xl p-5 border border-[#D4AF37]/30">
                    <h4 className="text-white font-medium mb-4">Estimated Returns</h4>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-white/60">Selected Tier</span>
                        <span className={`tier-badge ${selectedTier.color}`}>{selectedTier.name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-white/60">APY Rate</span>
                        <span className="text-[#D4AF37] font-bold">{selectedTier.apy}%</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-white/60">Estimated Rewards</span>
                        <span className="text-green-400 font-bold">+{potentialRewards.toFixed(2)} AIH</span>
                      </div>
                      <div className="border-t border-white/10 pt-3 flex justify-between">
                        <span className="text-white font-medium">Total Return</span>
                        <span className="text-white font-bold">{totalReturn.toFixed(2)} AIH</span>
                      </div>
                    </div>
                  </div>

                  <Button 
                    className="w-full btn-primary text-lg py-6" 
                    onClick={handleStake}
                    disabled={isStaking || !stakeAmount}
                    data-testid="stake-button"
                  >
                    <Lock className="mr-2" size={20} />
                    {isStaking ? 'Staking...' : `Stake ${selectedTier.name}`}
                  </Button>
                </TabsContent>

                <TabsContent value="unstake" className="space-y-6">
                  <div>
                    <label className="text-white/80 text-sm mb-2 block">Amount to Unstake</label>
                    <Input
                      type="number"
                      placeholder="Enter amount"
                      value={unstakeAmount}
                      onChange={(e) => setUnstakeAmount(e.target.value)}
                      className="input-cosmic text-lg"
                      data-testid="unstake-amount-input"
                    />
                    <div className="flex justify-between mt-2">
                      <span className="text-xs text-white/40">
                        Staked: {(user?.staked_tokens || 0).toLocaleString()} AIH
                      </span>
                      <button 
                        onClick={() => setUnstakeAmount(user?.staked_tokens?.toString() || '0')}
                        className="text-xs text-[#D4AF37] hover:underline"
                      >
                        Max
                      </button>
                    </div>
                  </div>

                  <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 flex items-start gap-3">
                    <Info className="text-yellow-400 flex-shrink-0 mt-0.5" size={18} />
                    <div>
                      <p className="text-yellow-400 font-medium">Early Unstaking</p>
                      <p className="text-white/60 text-sm">
                        Unstaking before the lock period may result in reduced rewards.
                      </p>
                    </div>
                  </div>

                  <Button 
                    className="w-full btn-secondary text-lg py-6" 
                    onClick={handleUnstake}
                    disabled={isUnstaking || !unstakeAmount}
                    data-testid="unstake-button"
                  >
                    <Unlock className="mr-2" size={20} />
                    {isUnstaking ? 'Unstaking...' : 'Unstake Tokens'}
                  </Button>
                </TabsContent>
              </Tabs>
            </div>

            {/* Features */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="glass-panel p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                  <Shield className="text-green-400" size={20} />
                </div>
                <div>
                  <p className="text-white font-medium text-sm">Secure</p>
                  <p className="text-xs text-white/60">Audited contracts</p>
                </div>
              </div>
              <div className="glass-panel p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <Zap className="text-blue-400" size={20} />
                </div>
                <div>
                  <p className="text-white font-medium text-sm">Instant</p>
                  <p className="text-xs text-white/60">Real-time rewards</p>
                </div>
              </div>
              <div className="glass-panel p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                  <Clock className="text-purple-400" size={20} />
                </div>
                <div>
                  <p className="text-white font-medium text-sm">Flexible</p>
                  <p className="text-xs text-white/60">Multiple durations</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Staking;
